// webgl-renderer.ts

import { PipelineConfig } from '../config-types';
import { FrameData, LadonOutput } from '../generator';
import { logger } from '../logger';
import { bilinearUpsample } from '../util';

type TextureInitializer = () => void;
export abstract class WebGLRenderer {
    protected intermediate_canvas?: HTMLCanvasElement;
    protected output_canvas_2d?: CanvasRenderingContext2D;

    protected gl: WebGL2RenderingContext;

    protected image_texture!: WebGLTexture;
    protected mask_texture!: WebGLTexture;

    protected verts = new Float32Array([
        -1.0,
        -1.0,
        0.0, // Vertex 1
        -1.0,
        1.0,
        0.0, // Vertex 2
        1.0,
        1.0,
        0.0, // Vertex 3
        1.0,
        1.0,
        0.0, // Vertex 4
        1.0,
        -1.0,
        0.0, // Vertex 5
        -1.0,
        -1.0,
        0.0, // Vertex 6
    ]);

    protected vertex_shader_src = `#version 300 es

in vec4 position;

void main() {
    gl_Position = position;
}
`;

    // Shared fragment shader code
    protected fragment_shader_header = `#version 300 es
precision highp float;

out vec4 outColor;

uniform sampler2D u_image;
uniform sampler2D u_mask;
uniform vec2 u_resolution;
`;

    // Shared shader functions for enhanced edge blending
    protected shared_shader_functions = `
float get_mask(vec2 uv) {
    float maskValue = texture(u_mask, uv).r;
    return smoothstep(0.0, 1.0, maskValue);
}
`;

    constructor(public output_canvas: HTMLCanvasElement, public readonly config: PipelineConfig) {
        const gl = this.output_canvas.getContext('webgl2', { antialias: true });

        if (!gl) {
            const output_2d = this.output_canvas.getContext('2d');

            if (!output_2d) {
                logger.errorAndThrow('Output canvas context must be webgl2 or 2d');
            }

            this.output_canvas_2d = output_2d;
            this.intermediate_canvas = document.createElement('canvas');
            this.intermediate_canvas.height = this.config.input.height;
            this.intermediate_canvas.width = this.config.input.width;

            const gl_intermediate = this.intermediate_canvas.getContext('webgl2', {
                antialias: true,
            });

            if (!gl_intermediate) {
                logger.errorAndThrow('Error getting output context webgl');
            }

            this.gl = gl_intermediate;
        } else {
            this.gl = gl;
        }

        // Initialize textures and buffers
        this.initTexturesAndBuffers();
    }

    protected initTexturesAndBuffers() {
        // Initialize image texture
        this.image_texture = this.createTexture(
            this.gl.RGBA,
            this.config.input.width,
            this.config.input.height
        );

        // Initialize mask texture
        this.mask_texture = this.createTexture(
            this.gl.R8,
            this.config.mask.upscaleSize.width,
            this.config.mask.upscaleSize.height
        );

        // Create and bind vertex array
        const vertex_array = this.gl.createVertexArray();
        if (!vertex_array) {
            logger.errorAndThrow('Error creating vertex array');
        }
        this.gl.bindVertexArray(vertex_array);

        // Create and bind buffer
        const buffer = this.gl.createBuffer();
        if (!buffer) {
            logger.errorAndThrow('Error creating buffer');
        }
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffer);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, this.verts, this.gl.STATIC_DRAW);
        this.gl.vertexAttribPointer(0, 3, this.gl.FLOAT, false, 0, 0);
        this.gl.enableVertexAttribArray(0);

        this.gl.viewport(0, 0, this.config.input.width, this.config.input.height);
    }

    protected createTexture(
        internalFormat: number,
        width: number,
        height: number,
        initializeTexture: TextureInitializer = () => {
            // Default initializer creates an empty texture
            this.gl.texImage2D(
                this.gl.TEXTURE_2D,
                0,
                internalFormat,
                width,
                height,
                0,
                internalFormat === this.gl.RGBA ? this.gl.RGBA : this.gl.RED,
                this.gl.UNSIGNED_BYTE,
                null
            );
        }
    ): WebGLTexture {
        const texture = this.gl.createTexture();
        if (!texture) {
            logger.errorAndThrow('Error creating texture');
        }

        this.gl.bindTexture(this.gl.TEXTURE_2D, texture);

        // Set texture parameters
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);

        // Initialize the texture using the provided function
        initializeTexture();

        this.gl.bindTexture(this.gl.TEXTURE_2D, null);
        return texture;
    }

    protected getUVCalculation() {
        if (this.config.render.horizontalMirror) {
            // Flip both x and y coordinates when horizontal mirroring
            return `vec2 st = vec2(1.0 - gl_FragCoord.x / u_resolution.x, 1.0 - gl_FragCoord.y / u_resolution.y);`;
        }

        // Default behavior (no mirroring)
        return `vec2 st = vec2(gl_FragCoord.x / u_resolution.x, 1.0 - gl_FragCoord.y / u_resolution.y);`;
    }

    protected setSharedUniforms(program: WebGLProgram) {
        const u_resolution = this.gl.getUniformLocation(program, 'u_resolution');
        if (!u_resolution) {
            logger.errorAndThrow('Error finding "u_resolution" uniform');
        }
        this.gl.uniform2f(u_resolution, this.config.input.width, this.config.input.height);

        const u_image = this.gl.getUniformLocation(program, 'u_image');
        if (!u_image) {
            logger.errorAndThrow('Error finding "u_image" uniform');
        }
        this.gl.activeTexture(this.gl.TEXTURE0);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.image_texture);
        this.gl.uniform1i(u_image, 0);

        const u_mask = this.gl.getUniformLocation(program, 'u_mask');
        if (u_mask) {
            this.gl.activeTexture(this.gl.TEXTURE1);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.mask_texture);
            this.gl.uniform1i(u_mask, 1);
        }
    }

    protected uploadVideoStream(input: FrameData) {
        this.gl.activeTexture(this.gl.TEXTURE0);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.image_texture);

        // Upload video frame or image data to the texture
        if (input instanceof HTMLVideoElement) {
            this.gl.texImage2D(
                this.gl.TEXTURE_2D,
                0,
                this.gl.RGBA,
                this.gl.RGBA,
                this.gl.UNSIGNED_BYTE,
                input
            );
        } else {
            this.gl.texImage2D(
                this.gl.TEXTURE_2D,
                0,
                this.gl.RGBA,
                this.config.input.width,
                this.config.input.height,
                0,
                this.gl.RGBA,
                this.gl.UNSIGNED_BYTE,
                input
            );
        }
    }

    protected uploadMask(mask?: LadonOutput) {
        // The mask may not be included if it has not been generated (e.g., frame skipped).
        if (mask?.warm) {
            const maskData = bilinearUpsample(
                mask.data,
                this.config.mask.outputSize.width,
                this.config.mask.outputSize.height,
                this.config.mask.upscaleSize.width,
                this.config.mask.upscaleSize.height
            );

            this.gl.activeTexture(this.gl.TEXTURE1);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.mask_texture);
            this.gl.texImage2D(
                this.gl.TEXTURE_2D,
                0,
                this.gl.R8,
                this.config.mask.upscaleSize.width,
                this.config.mask.upscaleSize.height,
                0,
                this.gl.RED,
                this.gl.UNSIGNED_BYTE,
                maskData
            );
        }
    }

    protected postDraw() {
        if (this.output_canvas_2d && this.intermediate_canvas) {
            this.output_canvas_2d.drawImage(
                this.intermediate_canvas,
                0,
                0,
                this.config.input.width,
                this.config.input.height
            );
        }
    }
}
